import { useEffect, useMemo } from 'react';

import { css } from '@emotion/css';
import { useFormContext } from 'react-hook-form';

import { useStyles } from '@/hooks/useStyles';
import { CarePlanDirectives } from '@/models/CarePlanDirectivesModel.ts';
import { ConditionType, MedicationSchema } from '@/models/ConditionModel';
import { TelemonitoringCriteriaTranslationMap } from '@/models/TelemonitoringCriteriaModel.ts';

import { DropDown } from './ControlledDropDown';

type TelemonitoringCriteriaPickProps = {
  carePlanDirectives: CarePlanDirectives;
};

export const TelemonitoringCriteriaPick = ({
  carePlanDirectives,
}: TelemonitoringCriteriaPickProps) => {
  const styles = useStyles(makeStyles);

  const context = useFormContext();
  const insulinType: MedicationSchema | undefined =
    context.watch('insulinScheme');
  const diabetesType: ConditionType | undefined = context.watch('diabetesType');

  const criteriaChoice = useMemo((): {
    value: string;
    translationKey: string;
  }[] => {
    if (
      insulinType &&
      diabetesType &&
      carePlanDirectives.telemonitoringCriteriaMap &&
      carePlanDirectives.telemonitoringTierMap
    ) {
      const criteriaChoiceByDiabetesType =
        carePlanDirectives.telemonitoringCriteriaMap[diabetesType];

      return insulinType in criteriaChoiceByDiabetesType
        ? criteriaChoiceByDiabetesType[insulinType].map(criteria => ({
            value: criteria,
            translationKey: TelemonitoringCriteriaTranslationMap[criteria],
          }))
        : [];
    }
    return [];
  }, [
    insulinType,
    diabetesType,
    carePlanDirectives.telemonitoringCriteriaMap,
    carePlanDirectives.telemonitoringTierMap,
  ]);

  useEffect(() => {
    if (criteriaChoice.length === 1) {
      context.setValue('inclusionCriteria', criteriaChoice[0].value);
    }
  }, [criteriaChoice]);

  return (
    <DropDown
      id="tls-criteria-pick"
      classes={{
        root: styles.container,
        dropdown: styles.dropdown,
        select: styles.select,
      }}
      fieldName="inclusionCriteria"
      choices={criteriaChoice}
      disabled={!criteriaChoice || !diabetesType || !insulinType}
      onClick={() => {
        if (!diabetesType) {
          context.setValue('diabetesType', diabetesType, {
            shouldTouch: true,
            shouldDirty: true,
            shouldValidate: true,
          });
        }
        if (!insulinType) {
          context.setValue('insulinScheme', insulinType, {
            shouldTouch: true,
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      }}
    />
  );
};

const makeStyles = () => ({
  container: css`
    display: flex;
    flex-direction: row;
    flex: 1 1 0;
    overflow: clip;
    white-space: wrap;
  `,
  dropdown: css`
    flex: 1 1 0;
    min-width: 0;
  `,
  select: css`
    white-space: wrap !important;
  `,
});
